import React, { useState, useEffect, useRef } from "react";

import { TextField } from "@project-stories/elements/TextField/TextField";
import { ProductTile } from "@project-stories/ecommerce/Product/ProductTile";
import Slider from '@src/stories/client/Slider/Slider';

import { ShopifySearchProvider } from "@src/providers/ShopifySearchProvider";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";

import "alpinejs";

const dispatchCustomEvent = (eventName, detail = { message: "" }) => {
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
};

export const HeaderSearch = ({ settings }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);
  const [hasNoResults, setHasNoResults] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  
  // Get search block settings or fall back to main section settings
  let searchBlock = settings?.blocks?.find(block => block.type === "search_bar") || {};

  if (!searchBlock){
    return false;
  }

  searchBlock = searchBlock.settings || searchBlock;
  // Merge settings with fallbacks
  const searchSettings = {
    enable_searchbar: searchBlock.enable_searchbar !== undefined ? searchBlock.enable_searchbar : false,
    search_bar_position: searchBlock.search_bar_position || "horizontal",
    search_bar_position_mobile: searchBlock.search_bar_position_mobile || "below_header",
    search_placeholder: searchBlock.search_placeholder || "Search...",
    search_icon: searchBlock.search_icon || "icon-magnifying-glass-thin",
    close_icon: searchBlock.close_icon || "icon-X",
    show_search_button: searchBlock.show_search_button !== undefined ? searchBlock.show_search_button : true,
    action_text: searchBlock.search_action_text || "SEARCH",
    show_text_no_icons_search: searchBlock.show_text_no_icons_search !== undefined ? 
      searchBlock.show_text_no_icons_search : false,
    search_layout: searchBlock.search_layout || "stacked",
    font_type: searchBlock.font_type || settings?.section?.header_font_type || "h2"
  };

  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const initialSuggestions = searchBlock.initial_suggestions?.split(',') || [];
  const trendingSearchTerms = searchBlock.trending_search_terms?.split(',') || [];

  // Handle click outside to close expanded search
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isExpanded && searchRef.current && !searchRef.current.contains(event.target)) {
        closeSearch();
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  // Setup event listeners for search results
  useEffect(() => {
    const handleSearchResults = (event) => {
      const { products, articles, queries } = event.detail;

      let suggestions = initialSuggestions;

      if (queries && queries.length > 0) {
        suggestions = queries.map(query => query.text) || [];
      }

      setKeywordSuggestions((suggestions).slice(0, 3));
      setArticles((articles || []).slice(0, 3));
      setProducts(products || []);
      setHasNoResults(products.length === 0 && articles.length === 0);
      setIsExpanded(true);
    };

    window.addEventListener("header-search-complete", handleSearchResults);
    window.addEventListener("searchbar-close", closeSearch);
    
    return () => {
      window.removeEventListener("header-search-complete", handleSearchResults);
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setHasNoResults(false);
    setSearchTerm(value);

    // Update predictive search if available
    const predictiveInput = document.getElementById("predictive-search-input");
    if (predictiveInput) {
      predictiveInput.value = value;
      const event = new Event("input", { bubbles: true });
      predictiveInput.dispatchEvent(event);
    }

    // Trigger search if value is not empty
    if (value.trim() && value.length > 1) {
      searchWithValue(value);
    }
  };

  const searchWithValue = (query) => {
    if (query.trim()) {
      const data = {
        detail: {
          query: query,
          type: "header",
          productsPerPage: settings.results_returned_header || 12
        }
      };

      // Dispatch search events
      window.dispatchEvent(
        new CustomEvent("searchrequest", data)
      );

      // Expand the search component
      setIsExpanded(true);
    }
  };

  const toggleSearchVisibility = () => {
    dispatchCustomEvent("searchbar-open");

    const newVisibility = !isSearchVisible;
    setIsSearchVisible(newVisibility);
    
    if (newVisibility) {
      setTimeout(() => {
        if (inputRef.current) {
          const input = inputRef.current.querySelector('input[name="q"]');
          input.focus();
        }
      }, 300);
    }
  };

  const closeSearch = () => {
    document.body.classList.remove("search-bar-active");
    document.body.classList.remove("dim-header-menu");

    setIsExpanded(false);
    setIsSearchVisible(false);
    setSearchTerm('');
    setProducts([]);
    setArticles([]);
    setKeywordSuggestions(initialSuggestions);

    if (inputRef.current) {
      const input = inputRef.current.querySelector('input[name="q"]');
      input.value = '';
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
    }
  };

  // If search bar position is horizontal, always show the search input
  const shouldAlwaysShow = isMobile
    ? false
    : searchSettings.search_bar_position === "horizontal";

  const handleSearchBarFocus = () => {
    document.body.classList.add("search-bar-active");
    document.body.classList.add("dim-header-menu");
    setIsSearchVisible(true);
  }

  const handleSearchBarBlur = () => {
    if (isExpanded) return;
    // closeSearch();
  }

  // The search content to be wrapped with ShopifySearchProvider
  const searchContent = (
    <>
      {searchSettings.enable_searchbar && (
        <div 
          ref={searchRef}
          className={`header-search-container ${shouldAlwaysShow ? 'always-visible flex-grow justify-end' : ''} ${isExpanded ? 'is-expanded' : ''}`}
        >
          {/* Search Toggle (only shown in vertical mode) */}
          {!shouldAlwaysShow && (
            <div 
              className="flex items-center cursor-pointer header-search-toggle" 
              onClick={toggleSearchVisibility}
              aria-label="Open search"
            >
              {searchSettings.show_text_no_icons_search ? (
                <span className={`${searchSettings.font_type}`}>
                  {searchSettings.action_text}
                </span>
              ) : (
                <i className={`${searchSettings.search_icon} mobile_menu-icon`}></i>
              )}
            </div>
          )}

          {/* Search Input Field */}
          {(shouldAlwaysShow || isSearchVisible) && (
            <div className={`header-search-wrapper ${shouldAlwaysShow ? 'always-show' : ''}`}>
              <form 
                action="/search" 
                method="get" 
                role="search"
                className={`header-search-form flex items-center ${isExpanded ? 'expanded' : ''}`}
                onSubmit={handleSearchSubmit}
              >
                <div
                  ref={inputRef}
                  className="header-search-input-container flex items-center relative w-full"
                >
                  <TextField
                    id="header-search"
                    name="q"
                    type="default-input" 
                    iconLeft={searchSettings.search_icon}
                    placeholder={searchSettings.search_placeholder}
                    className={`header-search-input transition-all duration-300 ${isExpanded ? 'expanded-input' : ''}`}
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => handleSearchBarFocus()}
                    onBlur={() => handleSearchBarBlur()}
                  />

                  {searchSettings.show_search_button && (
                    <button 
                      type="submit" 
                      className="header-search-submit"
                      aria-label="Search"
                    >
                      <i className={`${searchSettings.search_icon} text-[18px]`}></i>
                    </button>
                  )}

                  <button 
                    type="button"
                    className="header-search-close absolute right-2 z-10 items-center gap-1 hidden"
                    aria-label="Close search"
                    onClick={() => closeSearch()}
                  >
                    <span className="b4 underline hidden max-lg:inline xl:inline">Close</span>
                    <i className={`${searchSettings.close_icon} text-2xl`}></i>
                  </button>
                </div>
              </form>
              
              {/* Search Results Panel */}
              {(isExpanded && isSearchVisible) && (
                <div className="header-search-results">
                  <div className="header-search-results__overlay max-lg:hidden"></div>
                  <div className="header-search-results__content container">
                    {(!!keywordSuggestions.length || !!articles.length || !!products.length) && (
                      <div className="header-search-results__sidebar">
                        {trendingSearchTerms.length > 0 && (
                          <div className="flex lg:hidden gap-6 items-center overflow-x-auto pb-1 min-h-10">
                            <h6>Trending:</h6>

                            {trendingSearchTerms.map((suggestion, index) => (
                              <div key={index} className="b3--light shrink-0">
                                <a href={`/search?q=${encodeURIComponent(suggestion)}`}>
                                  {suggestion}
                                </a>
                              </div>
                            ))}
                          </div>
                        )}

                        {keywordSuggestions.length > 0 && (
                          <div className="max-lg:bg-rhv-cream max-lg:p-3 max-lg:rounded-xl">
                            <h6 className="h7 mb-3">
                              Suggestions:
                            </h6>

                            <ul className="flex flex-col gap-3">
                              {keywordSuggestions.map((suggestion, index) => (
                                <li key={`${suggestion}_${index}`} className="b3--light">
                                  <a href={`/search?q=${encodeURIComponent(suggestion)}`}>
                                    {suggestion}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {products.length > 0 && (
                          <ul className="lg:hidden flex items-center gap-2">
                            {products.slice(0,2)?.map((product, index) => (
                              <li
                                key={`product_${index}_${product.id}`}
                              >
                                <ProductTile
                                  settings={settings}
                                  product={product}
                                />
                              </li>
                            ))}
                          </ul> 
                        )}

                        {articles.length > 0 && (
                          <div>
                            <h6 className="h7 mb-3">
                              Articles:
                            </h6>

                            <ul className="flex flex-col gap-3">
                              {articles.map((article) => (
                                <li
                                  key={article.id}
                                  className="b3--light flex flex-col gap-2"
                                >
                                  <div className="flex gap-2 items-center">  
                                    {article.image?.src &&
                                      <a
                                        href={article.onlineStoreUrl}
                                        className="w-10 h-10 block shrink-0 rounded-lg overflow-hidden"
                                      >
                                        <img
                                          src={article.image?.src}
                                          alt={article.title}
                                          className="w-full h-full object-cover"
                                        />
                                      </a>
                                    }

                                    <a
                                      href={article.onlineStoreUrl}
                                      className="title-1"
                                    >
                                      {article.title}
                                    </a>
                                  </div>

                                  <div className="py-2.5">
                                    <a
                                      href={article.onlineStoreUrl}
                                      className="b3 underline"
                                    >
                                      Read More
                                    </a>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {(articles?.length > 0 || products?.length > 0) && (
                          <div className="py-2.5">
                            <a
                              href={`/search?q=${encodeURIComponent(searchTerm)}`}
                              className="w-full block"
                            >
                              <button className="button button-secondary w-full">
                                See All Search Results
                              </button>
                            </a>
                          </div>
                        )}

                        {hasNoResults && searchTerm && (
                          <p className="lg:hidden b2--bold">
                            No results found for "{searchTerm}"
                          </p>
                        )}
                      </div>
                    )}

                    <div className="w-full py-8 pl-16 overflow-auto max-lg:hidden">
                      {trendingSearchTerms.length > 0 && (
                        <div className="flex gap-6 mb-[18px] items-center">
                          <h6>Trending:</h6>

                          {trendingSearchTerms.map((suggestion, index) => (
                            <div key={index} className="b3--light">
                              <a href={`/search?q=${encodeURIComponent(suggestion)}`}>
                                {suggestion}
                              </a>
                            </div>
                          ))}
                        </div>
                      )}

                      {hasNoResults && searchTerm && (
                        <p className="max-lg:hidden b2--bold py-8">
                          No results found for "{searchTerm}"
                        </p>
                      )}

                      {products.length > 0 && (
                        <div className="relative">
                          <Slider>
                            {products.map((product, index) => (
                              <div
                                key={`product_${index}_${product.id}`}
                                className="h-full"
                              >
                                <ProductTile
                                  settings={settings}
                                  product={product}
                                />
                              </div>
                            ))}
                  
                            {products.length > 2 && (
                              <div className="lg:pl-8 xl:pl-16"></div>
                            )}
                          </Slider>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <ShopifySearchProvider settings={settings} searchType="header">
      {searchContent}
    </ShopifySearchProvider>
  );
};